import { useState, useEffect } from 'react';
import { useAdminStore } from '../store/adminStore';
import {
  Save,
  Store,
  Mail,
  Phone,
  MapPin,
  Truck,
  Shield,
  AlertTriangle,
} from 'lucide-react';

export default function AdminSettings() {
  const { settings, updateSettings, fetchSettingsFromAPI } = useAdminStore();

  useEffect(() => {
    fetchSettingsFromAPI();
  }, []);
  const [formData, setFormData] = useState({ ...settings });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateSettings(formData);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mağaza Ayarları</h1>
          <p className="text-gray-500 mt-1">Mağaza bilgilerini ve tercihlerini düzenleyin</p>
        </div>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-6 py-2.5 bg-brand-red hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
        >
          <Save className="w-4 h-4" />
          {saved ? 'Kaydedildi!' : 'Kaydet'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Info */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-6">
            <Store className="w-5 h-5 text-brand-red" />
            Mağaza Bilgileri
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Mağaza Adı</label>
              <input
                type="text"
                value={formData.storeName}
                onChange={(e) => setFormData({ ...formData, storeName: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-1">
                <Mail className="w-4 h-4" />
                E-posta
              </label>
              <input
                type="email"
                value={formData.storeEmail}
                onChange={(e) => setFormData({ ...formData, storeEmail: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-1">
                <Phone className="w-4 h-4" />
                Telefon
              </label>
              <input
                type="text"
                value={formData.storePhone}
                onChange={(e) => setFormData({ ...formData, storePhone: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-1">
                <MapPin className="w-4 h-4" />
                Adres
              </label>
              <textarea
                value={formData.storeAddress}
                onChange={(e) => setFormData({ ...formData, storeAddress: e.target.value })}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Shipping & Tax */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-6">
            <Truck className="w-5 h-5 text-brand-red" />
            Kargo ve Vergi
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Para Birimi</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              >
                <option value="TRY">₺ Türk Lirası (TRY)</option>
                <option value="USD">$ ABD Doları (USD)</option>
                <option value="EUR">€ Euro (EUR)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Kargo Ücreti (₺)</label>
              <input
                type="number"
                value={formData.shippingCost}
                onChange={(e) => setFormData({ ...formData, shippingCost: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              />
              <p className="text-xs text-gray-400 mt-1">Sabit kargo ücreti</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Ücretsiz Kargo Minimum (₺)</label>
              <input
                type="number"
                value={formData.freeShippingMin}
                onChange={(e) => setFormData({ ...formData, freeShippingMin: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              />
              <p className="text-xs text-gray-400 mt-1">Bu tutar ve üzeri siparişlerde ücretsiz kargo</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">KDV Oranı (%)</label>
              <input
                type="number"
                value={formData.taxRate}
                onChange={(e) => setFormData({ ...formData, taxRate: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
              />
            </div>
          </div>
        </div>

        {/* Maintenance Mode */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-6">
            <Shield className="w-5 h-5 text-brand-red" />
            Bakım Modu
          </h2>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-xl ${formData.maintenanceMode ? 'bg-yellow-100' : 'bg-green-100'}`}>
                <AlertTriangle className={`w-5 h-5 ${formData.maintenanceMode ? 'text-yellow-600' : 'text-green-600'}`} />
              </div>
              <div>
                <p className="font-medium text-gray-900">Bakım Modu</p>
                <p className="text-sm text-gray-500">
                  {formData.maintenanceMode
                    ? 'Mağaza şu anda bakım modunda. Ziyaretçiler bakım mesajı görecek.'
                    : 'Mağaza aktif. Ziyaretçiler normal alışveriş yapabilir.'}
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.maintenanceMode}
                onChange={(e) => setFormData({ ...formData, maintenanceMode: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-red/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-brand-red"></div>
            </label>
          </div>
          {formData.maintenanceMode && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-sm text-yellow-700">
                ⚠️ Bakım modu aktifken ana site ziyaretçilere bakım mesajı gösterecektir.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Save Button (Bottom) */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-6 py-2.5 bg-brand-red hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
        >
          <Save className="w-4 h-4" />
          {saved ? '✓ Kaydedildi!' : 'Değişiklikleri Kaydet'}
        </button>
      </div>
    </div>
  );
}
